import { type FC } from "react";
import { Control, UseFormRegister, UseFormWatch } from "react-hook-form";
import { PrescriptionTable } from "@/components/PrescriptionTable";
import { PrescriptionTitle } from "@/components/PrescriptionTitle";
import { TickIcon } from "@/components/Icons/TickIcon";
import { useTranslation } from "@/i18n";
import styles from "../steps/ManualInputStep/ManualInputStep.module.css";
import { ContactLensesData } from "@/types/config.types";
import { Skeleton } from "../UI/Skeleton";
import tablestyles from "./GlobalPrescriptionTable.module.css";

interface GlobalPrescriptionTableProps {
  contactLensesData: ContactLensesData;
  control?: Control<any>;
  register?: UseFormRegister<any>;
  samePrescription?: boolean;
  disableRemove?: boolean;
  modal?: boolean;
  highlightedAttribute?: string | null;
  handleRemoveLens?: (lens: "OD" | "OS") => void;
  watch?: UseFormWatch<any>;
  onChange?: (data: string, key: string) => void;
  isEditable?: boolean;
  showCheckbox?: boolean;
}

export const GlobalPrescriptionTable: FC<GlobalPrescriptionTableProps> = ({
  contactLensesData,
  control,
  register,
  samePrescription,
  disableRemove = false,
  modal = false,
  highlightedAttribute,
  handleRemoveLens,
  isEditable = true,
  showCheckbox = true,
}) => {
  const { t } = useTranslation();

  if (!contactLensesData) {
    return <Skeleton height={19} width="30%" />;
  }

  const hasOD = !!contactLensesData?.OD?.product;
  const hasOS = !!contactLensesData?.OS?.product;

  if (hasOD && !hasOS) {
    return (
      <PrescriptionTable
        control={control}
        contactLensesData={{ OD: contactLensesData.OD }}
        modal={modal}
        highlightedAttribute={highlightedAttribute}
        isEditable={isEditable}
      />
    );
  }

  if (hasOS && !hasOD) {
    return (
      <PrescriptionTable
        control={control}
        contactLensesData={{ OS: contactLensesData.OS }}
        modal={modal}
        highlightedAttribute={highlightedAttribute}
        isEditable={isEditable}
      />
    );
  }

  if (hasOD && hasOS) {
    const odId =
      contactLensesData?.OD?.product?.catalogEntryIdentifier?.parentProductId ||
      contactLensesData?.OD?.product?.catalogEntryIdentifier?.uniqueId;
    const osId =
      contactLensesData?.OS?.product?.catalogEntryIdentifier?.parentProductId ||
      contactLensesData?.OS?.product?.catalogEntryIdentifier?.uniqueId;

    if (odId && osId && odId === osId) {
      return (
        <>
          {showCheckbox && register && (
            <label className={styles.samePrescriptionLabel}>
              <input type="checkbox" {...register("samePrescription")} />
              <TickIcon color="" size={16} />
              <p>{t("steps.advancedPrescription.samePrescription")}</p>
            </label>
          )}
          <PrescriptionTable
            control={control}
            contactLensesData={contactLensesData}
            samePrescription={samePrescription}
            modal={modal}
            highlightedAttribute={highlightedAttribute}
            isEditable={isEditable}
          />
        </>
      );
    }

    return (
      <>
        <PrescriptionTitle
          product={contactLensesData?.OD?.product}
          disableRemove={disableRemove}
          handleRemoveProduct={handleRemoveLens}
          eye="OD"
        />
        <PrescriptionTable
          control={control}
          contactLensesData={{ OD: contactLensesData.OD }}
          modal={modal}
          highlightedAttribute={highlightedAttribute}
          isEditable={isEditable}
        />
        <PrescriptionTitle
          product={contactLensesData?.OS?.product}
          disableRemove={disableRemove}
          handleRemoveProduct={handleRemoveLens}
          eye="OS"
        />
        <PrescriptionTable
          control={control}
          contactLensesData={{ OS: contactLensesData.OS }}
          modal={modal}
          highlightedAttribute={highlightedAttribute}
          isEditable={isEditable}
        />
      </>
    );
  }

  return (
    <>
      <div className={tablestyles.imageTitleSkeleton}>
        <Skeleton height={70} width="15%" />
        <Skeleton height={40} width="80%" />
      </div>
      <Skeleton height={100} width="100%" />
    </>
  );
};
